import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissionContext } from '../../contexts/PermissionContext';
import { usePermission } from '../../hooks/usePermission';
import { FeatureKey } from '../../rbac/features';
import LoginPage from '../../pages/LoginPage';

// Centered spinner shown while permissions are resolving on a deep-route refresh. Rendering
// this (instead of bare null) keeps the user visually on the SAME route during the loading
// window — the <Navigate to="/"> below can only fire AFTER permLoading is false, so a
// refresh never flashes a bounce to the home dashboard.
const RouteSpinner: React.FC = () => (
  <Box
    sx={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <CircularProgress size={40} />
  </Box>
);

interface PermissionRouteProps {
  children: React.ReactNode;
  requiredFeature: FeatureKey;
  level?: 'view' | 'edit';
}

// Route guard driven by the RBAC matrix. Renders children when the caller has `level`
// (default 'view') on `requiredFeature`; otherwise REDIRECTS to the home dashboard ('/')
// instead of showing a dead-end "Unauthorized" page.
//
// Why redirect rather than show an Unauthorized panel: a user frequently lands on a
// forbidden route without choosing to — e.g. the previous user logged out while sitting
// on /admin/users and the next (lower-privilege) user logs in at that same URL, or a
// stale bookmark/refresh on a deep route. In those cases a dead-end "Unauthorized" screen
// is the wrong experience; the user should simply be taken to their own home, which the
// Dashboard ('/') is for every authenticated role (it is ungated and self-filters its
// content by capability). This does NOT weaken enforcement: the destination page is still
// never rendered for a user who lacks the capability, and the backend independently
// enforces every read/mutation. `/` is ungated so there is no redirect loop.
const PermissionRoute: React.FC<PermissionRouteProps> = ({ children, requiredFeature, level = 'view' }) => {
  const { user, isLoading } = useAuth();
  const { loading: permLoading } = usePermissionContext();
  const { can } = usePermission(requiredFeature);
  const location = useLocation();

  if (isLoading) return null; // let ProtectedRoute handle global loading
  if (!user) return <LoginPage />;
  // While permissions are still resolving, show a spinner rather than flashing a redirect.
  // This branch MUST stay strictly before the can() check below so a deep-route refresh can
  // never bounce to '/' during the loading window.
  if (permLoading) return <RouteSpinner />;

  if (!can(level)) {
    // Already at home somehow — render nothing rather than loop (defensive; '/' is ungated
    // so this branch shouldn't normally guard the home route).
    if (location.pathname === '/') return null;
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default PermissionRoute;
